import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Clock, Loader2, MessageSquare, Play, Repeat, Sparkles, Users, Workflow } from 'lucide-react';
import { AppChrome, toolLogo } from './AppChrome';
type PrimitiveId = 'chats' | 'shared' | 'workflows' | 'pulses' | 'skills';
const PRIMITIVES: Array<{
  id: PrimitiveId;
  label: string;
  icon: React.ElementType;
  copy: string;
}> = [{
  id: 'chats',
  label: 'AI chats',
  icon: MessageSquare,
  copy: 'A familiar chat interface, except it is signed into your tools. Enough to replace most standalone ChatGPT use.'
}, {
  id: 'shared',
  label: 'Team workspaces',
  icon: Users,
  copy: 'Tracks and groups keep work, files, and access together, so a project has one place instead of living in personal accounts.'
}, {
  id: 'workflows',
  label: 'Workflows',
  icon: Workflow,
  copy: 'Turn a process you repeat into defined steps with declared inputs, then run it on demand and see every run.'
}, {
  id: 'pulses',
  label: 'Pulses',
  icon: Repeat,
  copy: 'Work that runs without being asked — on a schedule, reporting into the channel that needs it.'
}, {
  id: 'skills',
  label: 'Skills',
  icon: Sparkles,
  copy: 'Show Giga how your company does something once, and it reuses that method everywhere else.'
}];
export function PlatformPillar() {
  const [active, setActive] = useState<PrimitiveId>('chats');
  return <div data-id="element-341" data-mp="1yh9uw59kq2x3">
      <div className="mx-auto max-w-3xl text-center" data-id="element-342" data-mp="1yh02kb123jo8">
        <p className="mb-4 text-[12px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-343" data-mp="1yjmawcvru37i">
          The platform
        </p>
        <h2 className="font-title text-4xl font-bold leading-[1.05] tracking-tight text-[#2B211A] md:text-[56px]" data-id="element-344" data-mp="28gim4qd213fr">
          Build the way your
          <br data-id="element-345" data-mp="23hfeqz9lql4m" />
          company works with AI.
        </h2>
        <p className="mx-auto mt-5 max-w-[54ch] text-lg leading-relaxed text-[#6B5D52]" data-id="element-346" data-mp="e1g39biigjlt">
          Giga is a full AI workspace, not infrastructure hiding behind a bot.
          These are the parts your company assembles into repeatable process
          instead of starting from a blank chat every time.
        </p>
      </div>

      <div className="mt-12 grid items-start gap-8 md:mt-16 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)] lg:gap-12" data-id="element-347" data-mp="12tdmcsgl345a">
        {/* Primitives as a list you step through: only the active one carries
         its explanation, so five ideas never shout at once. */}
        <ul className="space-y-1" data-id="element-348" data-mp="1hqedatqyav2p">
          {PRIMITIVES.map(p => {
          const on = active === p.id;
          return <li key={p.id} data-id="element-349" data-mp="1lqogxzr9t9ee">
                <button type="button" onClick={() => setActive(p.id)} aria-current={on ? 'true' : undefined} className={`w-full rounded-2xl px-4 py-3.5 text-left transition-colors ${on ? 'bg-white shadow-[0_18px_44px_-30px_rgba(43,33,26,0.5)] ring-1 ring-[#E7E0D4]' : 'hover:bg-white/60'}`} data-id="element-350" data-mp="1lpwubefnim09">
                  <span className="flex items-center gap-2.5" data-id="element-351" data-mp="20hxdytxi5x1k">
                    <p.icon size={17} className={on ? 'text-[#EA580C]' : 'text-neutral-400'} data-id="element-352" data-mp="25exll9a037us" />
                    <span className={`font-title text-xl font-bold tracking-tight ${on ? 'text-[#2B211A]' : 'text-[#6B5D52]'}`} data-id="element-353" data-mp="3py125c8keb1y">
                      {p.label}
                    </span>
                  </span>
                  {on && <motion.p initial={{
                opacity: 0,
                y: -4
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.22,
                ease: [0.23, 1, 0.32, 1]
              }} className="mt-2 pl-[27px] text-[15px] leading-relaxed text-[#6B5D52]" data-id="element-354" data-mp="3g0d6mk34wziq">
                      {p.copy}
                    </motion.p>}
                </button>
              </li>;
        })}
        </ul>

        <AppChrome active={active === 'pulses' ? 'Pulses' : active === 'shared' ? 'Groups' : undefined} data-id="element-355" data-mp="3tzmjpaw1ndih">
          <motion.div key={active} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.24,
          ease: [0.23, 1, 0.32, 1]
        }} className="px-5 py-5 md:px-7 md:py-6" data-id="element-356" data-mp="v9cmu3x91zxc">
            {active === 'chats' && <ChatsScreen data-id="element-357" data-mp="fzpq6g3b7fat" />}
            {active === 'shared' && <SharedScreen data-id="element-358" data-mp="fx3aft69lthi" />}
            {active === 'workflows' && <WorkflowsScreen data-id="element-359" data-mp="138flr2y24pb" />}
            {active === 'pulses' && <PulsesScreen data-id="element-360" data-mp="61a2yo7ekq6l" />}
            {active === 'skills' && <SkillsScreen data-id="element-361" data-mp="61s5u3okm2nh" />}
          </motion.div>
        </AppChrome>
      </div>
    </div>;
}
function ScreenHead({
  title,
  meta
}: {
  title: string;
  meta: string;
}) {
  return <div className="mb-5" data-id="element-362" data-mp="yow2nldifao6">
      <h3 className="font-title text-[21px] font-medium tracking-tight text-[#2B211A]" data-id="element-363" data-mp="yp5ulei47rwi">
        {title}
      </h3>
      <p className="mt-1 text-[12.5px] text-neutral-500" data-id="element-364" data-mp="jyyl3oa35j7y">{meta}</p>
    </div>;
}
function ChatsScreen() {
  return <div data-id="element-365" data-mp="oxrpmr7e9zvz">
      <ScreenHead title="Renewal risk in Salesforce" meta="Giga Master · 3 sources" data-id="element-366" data-mp="1cpoxk77q47rt" />
      <div className="flex justify-end" data-id="element-367" data-mp="1cnvqwrx41njt">
        <p className="max-w-[80%] rounded-2xl bg-[#F4F0E9] px-4 py-2.5 text-[13.5px] font-medium text-[#2B211A]" data-id="element-368" data-mp="1hgozxrzm3i1p">
          Which renewals due this quarter look at risk?
        </p>
      </div>
      <div className="mt-4 space-y-1.5" data-id="element-369" data-mp="2x8j25qi2v4ca">
        {[['salesforce', 'Read 34 opportunities closing this quarter'], ['intercom', 'Checked support volume per account'], ['gmail', 'Scanned the last 60 days of thread activity']].map(([slug, label]) => <p key={label} className="flex items-center gap-2 text-[12.5px] text-neutral-500" data-id="element-370" data-mp="36ycbslynt9dw">
            <img src={toolLogo(slug)} alt="" aria-hidden="true" className="size-3.5 shrink-0 object-contain" data-id="element-371" data-mp="2j8hy626ingsl" />
            {label}
            <Check size={12} strokeWidth={3} className="text-emerald-500" data-id="element-372" data-mp="2s8oavlue4cqp" />
          </p>)}
      </div>
      <div className="mt-4 space-y-3 text-[13.5px] leading-relaxed text-[#3D3229]" data-id="element-373" data-mp="2o72qlffj0w6h">
        <p data-id="element-374" data-mp="2o95riui7w9pz">
          <strong className="font-semibold text-[#2B211A]" data-id="element-375" data-mp="7ifdpr04k0jg">
            Three of eleven look at risk.
          </strong>{' '}
          Northwind has had no exec contact in 41 days and two unresolved
          tickets. Meridian's champion left in July. Fairline's usage is down
          38% month over month.
        </p>
        <p className="text-neutral-500" data-id="element-376" data-mp="319vupj6a94zo">
          I've drafted outreach for each owner — want me to send it?
        </p>
      </div>
      <div className="mt-5 flex flex-wrap gap-1.5 border-t border-[#EFEAE1] pt-3.5" data-id="element-377" data-mp="36atbbrbn9qz2">
        {['34 opportunities', '12 tickets', '60 days of mail'].map(s => <span key={s} className="rounded-full border border-[#EFEAE1] px-2.5 py-1 text-[11.5px] font-medium text-neutral-500" data-id="element-378" data-mp="2dkt1de2adnpc">
            {s}
          </span>)}
      </div>
    </div>;
}
function SharedScreen() {
  const groups = [{
    name: 'Giga Master',
    initials: 'GM',
    color: '#134E4A',
    members: 18,
    chats: 214,
    tracks: 33
  }, {
    name: 'Giga Marketing',
    initials: 'GM',
    color: '#7C2D12',
    members: 6,
    chats: 88,
    tracks: 12
  }, {
    name: 'NoContact Ops',
    initials: 'NO',
    color: '#1E3A5F',
    members: 4,
    chats: 51,
    tracks: 9
  }, {
    name: 'Everyone',
    initials: 'EV',
    color: '#3F3A2E',
    members: 18,
    chats: 106,
    tracks: 4
  }];
  return <div data-id="element-379" data-mp="1mcsvrrzc20ib">
      <ScreenHead title="Groups" meta="Access follows the group, not the item" data-id="element-380" data-mp="1medttrkuw1kx" />
      <ul className="space-y-2" data-id="element-381" data-mp="1ib7p4ode6igk">
        {groups.map(g => <li key={g.name} className="flex items-center gap-3 rounded-2xl border border-[#EFEAE1] px-3.5 py-3" data-id="element-382" data-mp="1i9wcdz1yy0db">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-xl text-[10.5px] font-bold text-white" style={{
          backgroundColor: g.color
        }} data-id="element-383" data-mp="1deqtza7rz3tz">
              {g.initials}
            </span>
            <span className="min-w-0 flex-1" data-id="element-384" data-mp="yngq3237sakr">
              <span className="block truncate text-[13.5px] font-semibold text-[#2B211A]" data-id="element-385" data-mp="yo9o9eo3xrra">
                {g.name}
              </span>
              <span className="font-mono text-[11px] text-neutral-400" data-id="element-386" data-mp="pn23lzeikpfo">
                spaces/{g.name.toLowerCase().replace(/\s+/g, '-')}
              </span>
            </span>
            <span className="flex shrink-0 items-center gap-1.5" data-id="element-387" data-mp="kq1xjohnzmok">
              {[[g.members, 'members'], [g.chats, 'chats'], [g.tracks, 'tracks']].map(([n, l]) => <span key={String(l)} title={String(l)} className="rounded-lg bg-[#F7F4EE] px-2 py-1 text-[11px] font-semibold text-neutral-500" data-id="element-388" data-mp="2djzy7f5to05m">
                  {n}
                </span>)}
            </span>
          </li>)}
      </ul>
    </div>;
}
function WorkflowsScreen() {
  const steps = ['Pull every deal that closed last week from Salesforce', 'Match each to its support and usage history', 'Write the win/loss summary into the Notion database', 'Post the digest to #revenue'];
  return <div data-id="element-389" data-mp="1gu0ovihukxlz">
      <ScreenHead title="Weekly win/loss digest" meta="4 steps · 26 runs · 96% success" data-id="element-390" data-mp="1gua5ifh05sf0" />
      <div className="mb-4 flex flex-wrap gap-1.5" data-id="element-391" data-mp="1kukmen6c238n">
        {[['Reporting period', 'Date · required'], ['Notion database', 'Database · required'], ['Owner', 'Text · optional']].map(([k, v]) => <span key={k} className="rounded-xl border border-[#EFEAE1] px-2.5 py-1.5" data-id="element-392" data-mp="1bu4lnlgz5a7h">
            <span className="block text-[12px] font-semibold text-[#2B211A]" data-id="element-393" data-mp="s8m7hnvp8zzc">
              {k}
            </span>
            <span className="text-[10.5px] text-neutral-400" data-id="element-394" data-mp="n9rfbvbudz2k">{v}</span>
          </span>)}
      </div>
      <ol className="space-y-2" data-id="element-395" data-mp="126ijehwgnyyi">
        {steps.map((s, i) => <li key={s} className="flex gap-3 rounded-2xl border border-[#EFEAE1] px-3.5 py-2.5" data-id="element-396" data-mp="1283b7zre53so" data-mpi={i}>
            <span className="mt-px flex size-5 shrink-0 items-center justify-center rounded-full bg-[#F4F0E9] text-[11px] font-bold text-[#2B211A]" data-id="element-397" data-mp="x6dymfoisi2h">
              {i + 1}
            </span>
            <span className="text-[13px] leading-snug text-[#3D3229]" data-id="element-398" data-mp="167uakruzgs8t">{s}</span>
          </li>)}
      </ol>
      <div className="mt-4 flex items-center gap-2 border-t border-[#EFEAE1] pt-3.5" data-id="element-399" data-mp="1kwqhump7u18d">
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-[#2B211A] px-3 py-1.5 text-[12px] font-bold text-white" data-id="element-400" data-mp="1fzhtu5hdz2z7">
          <Play size={11} data-id="element-401" data-mp="1tvvefnh7vkhw" />
          Run now
        </span>
        <span className="text-[12px] text-neutral-500" data-id="element-402" data-mp="1pxe3styl7rvq">
          Last run Monday 09:00 · 1m 12s
        </span>
      </div>
    </div>;
}
function PulsesScreen() {
  const pulses = [{
    name: 'Monday revenue brief',
    cadence: 'Mondays, 08:00',
    next: 'in 2 days',
    health: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
  }, {
    name: 'Ad spend anomaly check',
    cadence: 'Daily, 07:30',
    next: 'in 14 hours',
    health: [1, 1, 0, 1, 1, 1, 1, 1, 1, 1, 1, 2]
  }, {
    name: 'Support escalation sweep',
    cadence: 'Every 4 hours',
    next: 'in 51 minutes',
    health: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
  }, {
    name: 'Churn signal digest',
    cadence: 'Fridays, 16:00',
    next: 'in 6 days',
    health: [1, 1, 1, 1, 0, 1, 1, 1, 1, 1, 1, 1]
  }];
  return <div data-id="element-403" data-mp="3k6rmamujdvl4">
      <ScreenHead title="Pulses" meta="4 enabled · work that runs without being asked" data-id="element-404" data-mp="3k717lx4mzekh" />
      <ul className="space-y-2" data-id="element-405" data-mp="2cso752eymknn">
        {pulses.map(p => <li key={p.name} className="flex flex-wrap items-center gap-3 rounded-2xl border border-[#EFEAE1] px-3.5 py-3" data-id="element-406" data-mp="2cu95720hglq9">
            <span className="min-w-0 flex-1" data-id="element-407" data-mp="28tp6hu09ha20">
              <span className="block truncate text-[13.5px] font-semibold text-[#2B211A]" data-id="element-408" data-mp="oabp18f46fad">
                {p.name}
              </span>
              <span className="text-[11.5px] text-neutral-500" data-id="element-409" data-mp="s8sw5ryjbprs">
                {p.cadence}
              </span>
            </span>
            {/* Twelve ticks say "is this healthy" faster than a percentage. */}
            <span className="flex shrink-0 items-end gap-[3px]" data-id="element-410" data-mp="122jjgtixdu5w">
              {p.health.map((h, i) => <span key={i} className={`h-3.5 w-[3px] rounded-full ${h === 1 ? 'bg-emerald-400' : h === 0 ? 'bg-red-400' : 'bg-amber-400'}`} data-id="element-411" data-mp="171w2f36oeh37" data-mpi={i} />)}
            </span>
            <span className="flex shrink-0 items-center gap-1.5 text-[11.5px] font-medium text-neutral-500" data-id="element-412" data-mp="1g1j3tozenmw9">
              <Clock size={11} className="text-neutral-300" data-id="element-413" data-mp="1kz15qvf091ur" />
              {p.next}
            </span>
          </li>)}
      </ul>
      <div className="mt-3 flex items-center gap-2 rounded-2xl bg-[#FBF8F3] px-3.5 py-2.5 text-[12.5px] text-neutral-500" data-id="element-414" data-mp="3htximf6nkb8g">
        <Loader2 size={12} className="animate-spin text-[#EA580C]" data-id="element-415" data-mp="tw5xs9cs005b" />
        Support escalation sweep is running — step 2 of 4
      </div>
    </div>;
}
function SkillsScreen() {
  const skills = [{
    name: 'How we qualify an inbound lead',
    used: 142,
    group: 'Giga Marketing'
  }, {
    name: 'Our weekly reporting format',
    used: 96,
    group: 'Giga Master'
  }, {
    name: 'Escalating a P1 incident',
    used: 38,
    group: 'NoContact Ops'
  }, {
    name: 'Drafting a renewal email',
    used: 211,
    group: 'Giga Master'
  }];
  return <div data-id="element-416" data-mp="17vp3ywm111ez">
      <ScreenHead title="Skills" meta="Recorded once, reused everywhere" data-id="element-417" data-mp="17vynq2ghacc8" />
      <ul className="space-y-2" data-id="element-418" data-mp="1lmbui1hmvkzz">
        {skills.map(s => <li key={s.name} className="flex items-center gap-3 rounded-2xl border border-[#EFEAE1] px-3.5 py-3" data-id="element-419" data-mp="1hm07wj5pnalv">
            <Sparkles size={15} className="shrink-0 text-[#EA580C]" data-id="element-420" data-mp="1vl9pnn9kecry" />
            <span className="min-w-0 flex-1" data-id="element-421" data-mp="1vjggo1bhbggs">
              <span className="block truncate text-[13.5px] font-semibold text-[#2B211A]" data-id="element-422" data-mp="1qnredq82rlgs">
                {s.name}
              </span>
              <span className="text-[11.5px] text-neutral-500" data-id="element-423" data-mp="2a99u3s9003r1">{s.group}</span>
            </span>
            <span className="shrink-0 text-[11.5px] font-medium text-neutral-400" data-id="element-424" data-mp="2f6te78sdnaqw">
              used {s.used}×
            </span>
          </li>)}
      </ul>
      <p className="mt-3 text-[12.5px] leading-relaxed text-neutral-500" data-id="element-425" data-mp="258m2p5guxkwa">
        Every chat, workflow, and pulse in the workspace draws on these, so the
        method stays consistent no matter who asks.
      </p>
    </div>;
}